import type { Tables } from "@/integrations/supabase/types";
import { calculateExecution, describeExecution, type ExecutionResult } from "@/lib/executionEngine";

// === TYPES ===
export type MatchClassification = "alta_compatibilidade" | "media_compatibilidade" | "oportunidade" | "baixa";
export type FactorState = "confirmed" | "inferred" | "unknown";

export interface CompatibilityResult {
  score: number; // 0-100 (final = min of A→B, B→A)
  scoreAtoB: number;
  scoreBtoA: number;
  label: "Alta" | "Média" | "Baixa";
  classification: MatchClassification;
  confidence: number; // 0-100
  factors: { label: string; score: number; weight: number; state: FactorState }[];
  factorsBA: { label: string; score: number; weight: number; state: FactorState }[];
  explanation: { strengths: string[]; attention: string[]; improvements: string[] };
  valueDifference: number;
  valueDifferencePercent: number;
  /** Score de viabilidade prática (torna, composição, urgência, histórico). */
  executionScore: number;
  /** Score final de ranking que combina compatibilidade + execução. */
  rankingScore: number;
  execution: ExecutionResult;
}

type Property = Tables<"properties">;
type SwapPref = Tables<"swap_preferences">;

// === UNIFY PREFS (root columns ↔ desired_type_criteria JSONB) ===
// Some records store preferences only in the JSONB `desired_type_criteria`
// (multi-type editor) while the legacy root columns (min_value, desired_property_type, ...)
// stay null. The matching engine historically read only root columns and treated
// these properties as "no preferences", which leads to false positives.
// This helper merges both representations: root takes precedence; missing root
// fields are derived from the JSONB.
export function unifyPrefs(prefs: SwapPref | null): SwapPref | null {
  if (!prefs) return prefs;
  const criteria = (prefs as any).desired_type_criteria;
  if (!Array.isArray(criteria) || criteria.length === 0) return prefs;

  const merged: any = { ...prefs };

  // desired_property_type (union of types in JSONB, supports both `type` and `property_type` keys)
  if (!merged.desired_property_type || (Array.isArray(merged.desired_property_type) && merged.desired_property_type.length === 0)) {
    const types = Array.from(new Set(
      criteria.map((c: any) => c?.type ?? c?.property_type).filter((t: any) => !!t)
    ));
    if (types.length > 0) merged.desired_property_type = types;
  }

  // min_value (lowest of all type criteria)
  if (merged.min_value == null) {
    const mins = criteria.map((c: any) => c?.min_value).filter((v: any) => v != null && v !== "").map(Number);
    if (mins.length > 0) merged.min_value = Math.min(...mins);
  }

  // max_value (highest of all type criteria)
  if (merged.max_value == null) {
    const maxs = criteria.map((c: any) => c?.max_value).filter((v: any) => v != null && v !== "").map(Number);
    if (maxs.length > 0) merged.max_value = Math.max(...maxs);
  }

  // desired_cities (union)
  if (!merged.desired_cities || (Array.isArray(merged.desired_cities) && merged.desired_cities.length === 0)) {
    const cities = Array.from(new Set(
      criteria.flatMap((c: any) => Array.isArray(c?.cities) ? c.cities : []).filter((s: any) => typeof s === "string" && s.length > 0)
    ));
    if (cities.length > 0) merged.desired_cities = cities;
  }

  // desired_neighborhoods (union)
  if (!merged.desired_neighborhoods || (Array.isArray(merged.desired_neighborhoods) && merged.desired_neighborhoods.length === 0)) {
    const ngh = Array.from(new Set(
      criteria.flatMap((c: any) => Array.isArray(c?.neighborhoods) ? c.neighborhoods : []).filter((s: any) => typeof s === "string" && s.length > 0)
    ));
    if (ngh.length > 0) merged.desired_neighborhoods = ngh;
  }

  // max_cash_difference (max across criteria)
  if (merged.max_cash_difference == null) {
    const cd = criteria.map((c: any) => c?.max_cash_diff).filter((v: any) => v != null && v !== "").map(Number);
    if (cd.length > 0) merged.max_cash_difference = Math.max(...cd);
  }

  // min/max area
  if (merged.min_area_sqm == null) {
    const a = criteria.map((c: any) => c?.min_area).filter((v: any) => v != null && v !== "").map(Number);
    if (a.length > 0) merged.min_area_sqm = Math.min(...a);
  }
  if (merged.max_area_sqm == null) {
    const a = criteria.map((c: any) => c?.max_area).filter((v: any) => v != null && v !== "").map(Number);
    if (a.length > 0) merged.max_area_sqm = Math.max(...a);
  }

  // min bedrooms / bathrooms / parking (most permissive = lowest)
  if (merged.min_bedrooms == null) {
    const a = criteria.map((c: any) => c?.min_bedrooms).filter((v: any) => v != null && v !== "").map(Number);
    if (a.length > 0) merged.min_bedrooms = Math.min(...a);
  }
  if (merged.min_bathrooms == null) {
    const a = criteria.map((c: any) => c?.min_bathrooms).filter((v: any) => v != null && v !== "").map(Number);
    if (a.length > 0) merged.min_bathrooms = Math.min(...a);
  }
  if (merged.min_parking_spots == null) {
    const a = criteria.map((c: any) => c?.min_parking).filter((v: any) => v != null && v !== "").map(Number);
    if (a.length > 0) merged.min_parking_spots = Math.min(...a);
  }

  // desired_features (union)
  if (!merged.desired_features || (Array.isArray(merged.desired_features) && merged.desired_features.length === 0)) {
    const feats = Array.from(new Set(
      criteria.flatMap((c: any) => Array.isArray(c?.features) ? c.features : []).filter((s: any) => typeof s === "string" && s.length > 0)
    ));
    if (feats.length > 0) merged.desired_features = feats;
  }

  return merged as SwapPref;
}

// === CONSTANTS ===
const COMPLEMENTARY_REASONS: Record<string, string[]> = {
  imovel_menor: ["imovel_maior", "upgrade"],
  imovel_maior: ["imovel_menor", "downgrade"],
  downgrade: ["imovel_maior", "upgrade"],
  upgrade: ["imovel_menor", "downgrade"],
  mudanca_cidade: ["mudanca_cidade", "mudanca_bairro_cidade"],
  mudanca_bairro: ["mudanca_bairro", "mudanca_bairro_cidade"],
  mudanca_bairro_cidade: ["mudanca_bairro_cidade", "mudanca_cidade", "mudanca_bairro"],
  investimento: ["investimento"],
  capitalizar: ["capitalizar", "investimento"],
};
const DIRECTIONAL_REASONS = new Set(["imovel_menor", "imovel_maior", "upgrade", "downgrade"]);
const WANTS_SMALLER_REASONS = new Set(["imovel_menor", "downgrade"]);
const WANTS_LARGER_REASONS = new Set(["imovel_maior", "upgrade"]);
const DIRECTIONAL_SWAP_TYPES = new Set(["menor_mais_dinheiro", "maior_pagando_diferenca"]);
const WANTS_SMALLER_SWAP_TYPES = new Set(["menor_mais_dinheiro"]);
const WANTS_LARGER_SWAP_TYPES = new Set(["maior_pagando_diferenca"]);
const COMPATIBLE_SWAP_TYPE_PAIRS = new Set([
  "menor_mais_dinheiro:maior_pagando_diferenca",
  "maior_pagando_diferenca:menor_mais_dinheiro",
]);

// Pesos do CORE de compatibilidade — só fatores que definem se os
// imóveis fazem sentido entre si como ATIVOS PRINCIPAIS. Torna, direção
// e composição complementar foram movidas para o executionEngine.
const WEIGHTS = {
  type: 0.25,         // tipo principal aceito (núcleo)
  value: 0.25,        // faixa de valor
  location: 0.20,     // cidade / região
  minFeatures: 0.10,  // quartos / vagas / área mínimos declarados
  swapReason: 0.10,   // motivo da troca compatível
  conditions: 0.10,   // financiado / construção / parcial
};

function stateScore(state: FactorState): number {
  if (state === "confirmed") return 100;
  if (state === "inferred") return 70;
  return 30;
}

// === MIN FEATURES (quartos / vagas / áreas mínimas declaradas) ===
// Só pontua quando A declarou pelo menos um mínimo. Mínimo declarado e
// não atendido devolve hardMiss=true para o caller derrubar o score.
function scoreMinFeatures(
  prefsA: SwapPref | null,
  candidate: Property,
): { score: number; state: FactorState; hardMiss: boolean } {
  if (!prefsA) return { score: 60, state: "unknown", hardMiss: false };

  const checks: { req: number | null; got: number | null }[] = [
    { req: prefsA.min_bedrooms != null ? Number(prefsA.min_bedrooms) : null, got: candidate.bedrooms != null ? Number(candidate.bedrooms) : null },
    { req: prefsA.min_bathrooms != null ? Number(prefsA.min_bathrooms) : null, got: candidate.bathrooms != null ? Number(candidate.bathrooms) : null },
    { req: prefsA.min_parking_spots != null ? Number(prefsA.min_parking_spots) : null, got: candidate.parking_spots != null ? Number(candidate.parking_spots) : null },
    { req: prefsA.min_area_sqm != null ? Number(prefsA.min_area_sqm) : null, got: Number(candidate.built_area_sqm || candidate.area_sqm || 0) || null },
  ];

  const declared = checks.filter(c => c.req != null && c.req > 0);
  if (declared.length === 0) return { score: 60, state: "unknown", hardMiss: false };

  let met = 0;
  let missing = 0;
  for (const c of declared) {
    if (c.got == null) { missing++; continue; }
    if (c.got >= (c.req as number)) met++;
    else missing++;
  }
  const ratio = met / declared.length;
  const hardMiss = missing > 0 && met === 0; // nada atendido = bloqueio
  const score = Math.round(20 + ratio * 80);
  return { score, state: "confirmed", hardMiss };
}

// === OPERATION CONDITIONS (financiado / construção / parcial) ===
// Confirma se o candidato bate com o que A declarou aceitar.
function scoreOperationConditions(
  prefsA: SwapPref | null,
  candidate: Property,
): { score: number; state: FactorState; hardMiss: boolean } {
  if (!prefsA) return { score: 60, state: "unknown", hardMiss: false };

  const flags: { applies: boolean; ok: boolean }[] = [];

  // Financiado: se candidato é financiado e A não aceita financiado → conflito
  if (candidate.is_financed === true) {
    const accepts = (prefsA as any).accepts_financed !== false; // default tolerante
    flags.push({ applies: true, ok: accepts });
  }

  // Construção: se candidato está em construção e A não aceita
  if (candidate.construction_status && candidate.construction_status !== "pronto") {
    const accepts = (prefsA as any).accepts_under_construction !== false;
    flags.push({ applies: true, ok: accepts });
  }

  // Permuta parcial: se candidato exige permuta parcial e A não aceita
  // (Sinal indireto: ainda não há campo dedicado em properties; tratamos
  // como neutro se não há indicação clara.)

  if (flags.length === 0) {
    // Nada a checar especificamente: estado neutro/inferido a favor.
    return { score: 75, state: prefsA ? "inferred" : "unknown", hardMiss: false };
  }

  const okCount = flags.filter(f => f.ok).length;
  const ratio = okCount / flags.length;
  const hardMiss = okCount === 0;
  const score = Math.round(20 + ratio * 80);
  return { score, state: "confirmed", hardMiss };
}

// === HELPERS ===
function isValueDirectionConflict(r: string | null | undefined, offered: number, owner: number, smaller: Set<string>, larger: Set<string>) {
  if (!r) return false;
  if (smaller.has(r)) return offered > owner;
  if (larger.has(r)) return offered < owner;
  return false;
}

function areSwapTypesCompatible(a: string | null | undefined, b: string | null | undefined) {
  if (!a || !b) return true;
  if (a === "aberto" || b === "aberto") return true;
  if (a === b) return !DIRECTIONAL_SWAP_TYPES.has(a);
  return COMPATIBLE_SWAP_TYPE_PAIRS.has(`${a}:${b}`);
}

function getSwapReasonResult(myReason: string | null | undefined, candReason: string | null | undefined, myVal: number, candVal: number) {
  let score = 30, criticalConflict = false, hardConflict = false;
  let state: FactorState = "unknown";

  if (myReason && candReason) {
    state = "confirmed";
    const comp = COMPLEMENTARY_REASONS[myReason];
    if (comp?.includes(candReason)) score = 100;
    else if (myReason === candReason) {
      const dc = DIRECTIONAL_REASONS.has(myReason);
      score = dc ? 15 : 70; criticalConflict = dc;
    } else score = 30;
    if (isValueDirectionConflict(candReason, myVal, candVal, WANTS_SMALLER_REASONS, WANTS_LARGER_REASONS)) { score = Math.min(score, 20); hardConflict = true; }
    if (isValueDirectionConflict(myReason, candVal, myVal, WANTS_SMALLER_REASONS, WANTS_LARGER_REASONS)) { score = Math.min(score, 20); hardConflict = true; }
  } else if (myReason) {
    state = "inferred";
    if (isValueDirectionConflict(myReason, candVal, myVal, WANTS_SMALLER_REASONS, WANTS_LARGER_REASONS)) { score = 10; hardConflict = true; }
    else score = 70;
  } else if (candReason) {
    state = "inferred";
    if (isValueDirectionConflict(candReason, myVal, candVal, WANTS_SMALLER_REASONS, WANTS_LARGER_REASONS)) { score = 10; hardConflict = true; }
    else score = 70;
  }
  return { score, criticalConflict: criticalConflict || hardConflict, hardConflict, state };
}

function getSwapTypeResult(myType: string | null | undefined, candType: string | null | undefined, myVal: number, candVal: number) {
  let score = 30, criticalConflict = false, hardConflict = false;
  let state: FactorState = "unknown";

  if (myType && candType) {
    state = "confirmed";
    if (areSwapTypesCompatible(myType, candType)) score = 100;
    else if (myType === candType && DIRECTIONAL_SWAP_TYPES.has(myType)) { score = 15; criticalConflict = true; }
    else { score = 30; criticalConflict = true; }
    if (isValueDirectionConflict(candType, myVal, candVal, WANTS_SMALLER_SWAP_TYPES, WANTS_LARGER_SWAP_TYPES)) { score = Math.min(score, 20); hardConflict = true; }
    if (isValueDirectionConflict(myType, candVal, myVal, WANTS_SMALLER_SWAP_TYPES, WANTS_LARGER_SWAP_TYPES)) { score = Math.min(score, 20); hardConflict = true; }
  } else if (myType) {
    state = "inferred";
    if (isValueDirectionConflict(myType, candVal, myVal, WANTS_SMALLER_SWAP_TYPES, WANTS_LARGER_SWAP_TYPES)) { score = 10; hardConflict = true; }
    else score = 70;
  } else if (candType) {
    state = "inferred";
    if (isValueDirectionConflict(candType, myVal, candVal, WANTS_SMALLER_SWAP_TYPES, WANTS_LARGER_SWAP_TYPES)) { score = 10; hardConflict = true; }
    else score = 70;
  }
  return { score, criticalConflict: criticalConflict || hardConflict, hardConflict, state };
}

// === DIRECTIONAL SCORE ===
interface DirectionalResult {
  score: number;
  factors: { label: string; score: number; weight: number; state: FactorState }[];
  hasCritical: boolean;
  hasHard: boolean;
  states: Record<string, FactorState>;
}

function calcDirectional(propA: Property, prefsA: SwapPref | null, propB: Property, prefsB: SwapPref | null): DirectionalResult {
  const valA = Number(propA.estimated_value);
  const valB = Number(propB.estimated_value);
  const valueDiff = Math.abs(valA - valB);
  const factors: DirectionalResult["factors"] = [];
  const states: Record<string, FactorState> = {};

  // 1. TYPE (25%) — núcleo da compatibilidade do imóvel principal aceito.
  // "Outros" foi removido da taxonomia → não é mais usado como eixo principal.
  // Veículo NUNCA aparece aqui.
  let typeScore: number;
  const desiredTypes = (prefsA?.desired_property_type as string[] | null) || [];
  if (desiredTypes.length > 0) {
    states.type = "confirmed";
    typeScore = desiredTypes.includes(propB.property_type) ? 100 : 10; // hard mismatch
  } else if (prefsA?.accepts_any_type) {
    states.type = "inferred";
    typeScore = 75; // "indiferente" → não restringe, mas não é confirmação ativa
  } else {
    states.type = "unknown";
    typeScore = 35;
  }
  factors.push({ label: "Tipo de imóvel", score: typeScore, weight: WEIGHTS.type * 100, state: states.type });

  // 2. VALUE (25%) — faixa do imóvel principal aceito.
  let valueScore: number;
  const minV = prefsA?.min_value ? Number(prefsA.min_value) : null;
  const maxV = prefsA?.max_value ? Number(prefsA.max_value) : null;
  if (minV || maxV) {
    states.value = "confirmed";
    const inRange = (!minV || valB >= minV) && (!maxV || valB <= maxV);
    if (inRange) valueScore = 100;
    else {
      const diff = (minV && valB < minV) ? minV - valB : (maxV && valB > maxV) ? valB - maxV : 0;
      const pct = diff / Math.max(valA, 1) * 100;
      valueScore = pct <= 10 ? 50 : pct <= 20 ? 35 : 15;
    }
  } else {
    states.value = "unknown";
    const diffPct = valA > 0 ? (valueDiff / valA) * 100 : 100;
    valueScore = diffPct <= 20 ? 70 : diffPct <= 50 ? 50 : 30;
  }
  factors.push({ label: "Faixa de valor", score: valueScore, weight: WEIGHTS.value * 100, state: states.value });

  // 3. LOCATION (20%)
  let locationScore: number;
  if (prefsA?.desired_cities && (prefsA.desired_cities as string[]).length > 0) {
    states.location = "confirmed";
    const cityHit = (prefsA.desired_cities as string[]).includes(propB.city);
    // bairro como bônus se ambos declarados
    const desiredNg = (prefsA.desired_neighborhoods as string[] | null) || [];
    const ngHit = desiredNg.length > 0 && propB.neighborhood
      ? desiredNg.includes(propB.neighborhood)
      : false;
    if (cityHit && (desiredNg.length === 0 || ngHit)) locationScore = 100;
    else if (cityHit) locationScore = 80;
    else locationScore = 10;
  } else if (prefsA?.accepts_other_cities) {
    states.location = "inferred";
    locationScore = 70;
  } else {
    states.location = "unknown";
    locationScore = propA.city === propB.city ? 70 : 30;
  }
  factors.push({ label: "Localização", score: locationScore, weight: WEIGHTS.location * 100, state: states.location });

  // 4. MIN FEATURES (10%) — quartos, vagas, área mínimas.
  // Só pontua se A declarou um mínimo. Mínimo declarado e NÃO atendido = derruba.
  const minFeatRes = scoreMinFeatures(prefsA, propB);
  states.minFeatures = minFeatRes.state;
  factors.push({ label: "Características mínimas", score: minFeatRes.score, weight: WEIGHTS.minFeatures * 100, state: minFeatRes.state });

  // 5. SWAP REASON (10%)
  const reasonRes = getSwapReasonResult(prefsA?.swap_reason, prefsB?.swap_reason, valA, valB);
  states.swapReason = reasonRes.state;
  factors.push({ label: "Motivo da troca", score: reasonRes.score, weight: WEIGHTS.swapReason * 100, state: reasonRes.state });

  // 6. CONDITIONS (10%) — financiado / construção / permuta parcial.
  const condRes = scoreOperationConditions(prefsA, propB);
  states.conditions = condRes.state;
  factors.push({ label: "Condições da operação", score: condRes.score, weight: WEIGHTS.conditions * 100, state: condRes.state });

  // Intent (swap_type) — usado apenas como FLAG de conflito direcional, não como peso.
  const intentRes = getSwapTypeResult(prefsA?.swap_type, prefsB?.swap_type, valA, valB);

  // WEIGHTED TOTAL
  const totalWeight = factors.reduce((s, f) => s + f.weight, 0);
  const weightedScore = factors.reduce((s, f) => s + f.score * f.weight, 0) / totalWeight;
  let score = Math.round(weightedScore);

  if (reasonRes.criticalConflict || intentRes.criticalConflict) score = Math.min(score, 35);
  if (reasonRes.hardConflict || intentRes.hardConflict) score = Math.min(score, 20);

  // HARD CONSTRAINT: tipo declarado e não atendido derruba forte
  if (states.type === "confirmed" && typeScore <= 15) score = Math.min(score, 25);
  // HARD CONSTRAINT: característica mínima declarada e não atendida
  if (minFeatRes.hardMiss) score = Math.min(score, 30);
  // HARD CONSTRAINT: condição da operação incompatível (ex: A não aceita financiado e B é financiado)
  if (condRes.hardMiss) score = Math.min(score, 30);

  return { score, factors, hasCritical: reasonRes.criticalConflict || intentRes.criticalConflict, hasHard: reasonRes.hardConflict || intentRes.hardConflict, states };
}

// === CONFIDENCE ===
function calcConfidence(prefsA: SwapPref | null, prefsB: SwapPref | null, statesAB: Record<string, FactorState>, statesBA: Record<string, FactorState>): number {
  const prefFields = ["desired_cities", "desired_property_type", "min_value", "max_value", "swap_reason", "swap_type", "max_cash_difference", "urgency"];
  let filledA = 0, filledB = 0;
  for (const f of prefFields) {
    const va = (prefsA as any)?.[f];
    if (va !== null && va !== undefined && !(Array.isArray(va) && va.length === 0)) filledA++;
    const vb = (prefsB as any)?.[f];
    if (vb !== null && vb !== undefined && !(Array.isArray(vb) && vb.length === 0)) filledB++;
  }
  const fillRate = ((filledA + filledB) / (prefFields.length * 2)) * 100;

  let confirmedCount = 0, totalFactors = 0;
  for (const key of Object.keys(statesAB)) {
    totalFactors++;
    if (statesAB[key] === "confirmed" && statesBA[key] === "confirmed") confirmedCount++;
    else if (statesAB[key] === "confirmed" || statesBA[key] === "confirmed") confirmedCount += 0.5;
  }
  const bilateralRate = totalFactors > 0 ? (confirmedCount / totalFactors) * 100 : 0;
  const consistencyRate = (prefsA && prefsB) ? 100 : (prefsA || prefsB) ? 50 : 0;

  return Math.round(fillRate * 0.5 + bilateralRate * 0.3 + consistencyRate * 0.2);
}

function classifyMatch(score: number): MatchClassification {
  if (score >= 70) return "alta_compatibilidade";
  if (score >= 50) return "media_compatibilidade";
  if (score >= 30) return "oportunidade";
  return "baixa";
}

function buildExplanation(
  factorsAB: { label: string; score: number; state: FactorState }[],
  factorsBA: { label: string; score: number; state: FactorState }[],
  confidence: number,
): { strengths: string[]; attention: string[]; improvements: string[] } {
  const strengths: string[] = [];
  const attention: string[] = [];
  const improvements: string[] = [];

  const count = Math.min(factorsAB.length, factorsBA.length);
  for (let i = 0; i < count; i++) {
    const fAB = factorsAB[i];
    const fBA = factorsBA[i];
    const avg = (fAB.score + fBA.score) / 2;

    if (avg >= 80) strengths.push(`${fAB.label} com forte alinhamento bilateral`);
    else if (avg < 40) attention.push(`${fAB.label} apresenta divergência`);
    else if (avg < 60) attention.push(`${fAB.label} tem alinhamento parcial`);

    if (fAB.state === "unknown" || fBA.state === "unknown") {
      const side = fAB.state === "unknown" && fBA.state === "unknown"
        ? "Ambos os lados"
        : fAB.state === "unknown" ? "O outro lado" : "Você";
      improvements.push(`${side} ainda não definiu preferências de ${fAB.label.toLowerCase()}`);
    }
  }

  if (confidence < 50) improvements.push("Complete suas preferências de permuta para melhorar a precisão");

  return { strengths, attention, improvements };
}

// === MAIN FUNCTION ===
export function calculateCompatibility(
  myProperty: Property,
  myPrefs: SwapPref | null,
  candidateProperty: Property,
  candidatePrefs: SwapPref | null
): CompatibilityResult {
  // Merge root columns with desired_type_criteria JSONB so the engine sees
  // preferences regardless of which storage format the UI used.
  myPrefs = unifyPrefs(myPrefs);
  candidatePrefs = unifyPrefs(candidatePrefs);

  const valA = Number(myProperty.estimated_value);
  const valB = Number(candidateProperty.estimated_value);
  const valueDiff = Math.abs(valA - valB);
  const avg = (valA + valB) / 2;
  const valueDiffPct = avg > 0 ? (valueDiff / avg) * 100 : 100;

  // 🔄 DIRECTIONAL SCORES
  const ab = calcDirectional(myProperty, myPrefs, candidateProperty, candidatePrefs);
  const ba = calcDirectional(candidateProperty, candidatePrefs, myProperty, myPrefs);

  // FINAL = min(A→B, B→A)
  let score = Math.min(ab.score, ba.score);

  // 🔥 ASYMMETRY PENALTY
  const myFieldCount = countFilledPrefs(myPrefs);
  const candFieldCount = countFilledPrefs(candidatePrefs);
  const asymmetry = Math.abs(myFieldCount - candFieldCount);
  if (asymmetry >= 5 && (myFieldCount + candFieldCount) < 8) {
    score = Math.round(score * 0.7);
  }

  const confidence = calcConfidence(myPrefs, candidatePrefs, ab.states, ba.states);

  // === EXECUTION LAYER ===
  // Composição complementar SÓ entra depois que a compatibilidade principal
  // já passou de um piso mínimo. Abaixo disso, viabilidade não maquia
  // incompatibilidade estrutural.
  const COMPAT_MIN_FOR_EXECUTION = 35;
  const execution = calculateExecution({
    propertyA: myProperty,
    prefsA: myPrefs,
    propertyB: candidateProperty,
    prefsB: candidatePrefs,
    historySignal: 0,
  });
  const executionScore = score < COMPAT_MIN_FOR_EXECUTION
    ? Math.min(execution.score, 35)
    : execution.score;

  // Ranking final = 65% compatibilidade + 35% execução.
  // Cap se execução for muito ruim ou conflito de direção.
  let rankingScore = Math.round(score * 0.65 + executionScore * 0.35);
  if (execution.directionConflict) rankingScore = Math.min(rankingScore, 35);
  if (execution.coverage === "uncovered") rankingScore = Math.min(rankingScore, 35);

  const classification = classifyMatch(rankingScore);
  const explanation = buildExplanation(ab.factors, ba.factors, confidence);
  // Anexa frase de execução à explicação
  const execNote = describeExecution(execution);
  if (execution.directionConflict || execution.coverage === "uncovered") {
    explanation.attention.push(execNote);
  } else if (execution.coverage === "cash_only" || execution.coverage === "none") {
    explanation.strengths.push(execNote);
  } else {
    explanation.attention.push(execNote);
  }
  const label: CompatibilityResult["label"] = rankingScore >= 70 ? "Alta" : rankingScore >= 50 ? "Média" : "Baixa";

  return {
    score,
    scoreAtoB: ab.score,
    scoreBtoA: ba.score,
    label,
    classification,
    confidence,
    factors: ab.factors,
    factorsBA: ba.factors,
    explanation,
    valueDifference: valueDiff,
    valueDifferencePercent: Math.round(valueDiffPct * 10) / 10,
    executionScore,
    rankingScore,
    execution,
  };
}

function countFilledPrefs(prefs: SwapPref | null): number {
  if (!prefs) return 0;
  let count = 0;
  const fields = ["desired_cities", "desired_property_type", "min_value", "max_value", "swap_reason", "swap_type", "max_cash_difference", "urgency"];
  for (const f of fields) {
    const v = (prefs as any)[f];
    if (v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)) count++;
  }
  return count;
}

// === INFRASTRUCTURE SCORE ===
function calculateInfrastructureScore(myPrefs: SwapPref | null, candidate: Property): number {
  const desiredFeatures = myPrefs?.desired_features as string[] | null;
  if (!desiredFeatures || desiredFeatures.length === 0) {
    let bonus = 50;
    if (candidate.has_solar_energy) bonus += 8;
    if (candidate.has_habite_se) bonus += 7;
    if (!candidate.has_liens) bonus += 5;
    if (candidate.has_electronic_gate || candidate.has_electric_fence) bonus += 5;
    if (candidate.is_walled && candidate.has_paved_street) bonus += 5;
    return Math.min(100, bonus);
  }

  const featureMap: Record<string, (p: Property) => boolean> = {
    pool: p => p.has_pool, gourmet: p => p.has_gourmet_area, garden: p => p.has_garden,
    furnished: p => p.is_furnished, elevator: p => p.has_elevator,
    security: p => p.has_security || p.has_electronic_gate || p.has_electric_fence,
    balcony: p => p.has_balcony, bbq: p => p.has_bbq, office: p => p.has_office,
    air_conditioning: p => p.has_air_conditioning, pets: p => p.accepts_pets,
    leisure: p => p.has_leisure_area, solar_energy: p => p.has_solar_energy,
    walled: p => p.is_walled, paved_street: p => p.has_paved_street,
    electronic_gate: p => p.has_electronic_gate, electric_fence: p => p.has_electric_fence,
    condo_gym: p => p.condo_has_gym, condo_pool: p => p.condo_has_pool,
    condo_party_room: p => p.condo_has_party_room, condo_sports_court: p => p.condo_has_sports_court,
    condo_playground: p => p.condo_has_playground, condo_24h_security: p => p.condo_has_24h_security,
    condo_sauna: p => p.condo_has_sauna, condo_jacuzzi: p => p.condo_has_jacuzzi,
    condo_coworking: p => p.condo_has_coworking, condo_game_room: p => p.condo_has_game_room,
    condo_walking_trail: p => p.condo_has_walking_trail, condo_generator: p => p.condo_has_generator,
    condo_kids_pool: p => p.condo_has_kids_pool, condo_gourmet: p => p.condo_has_gourmet,
    condo_cinema: p => p.condo_has_cinema, condo_pet_space: p => p.condo_has_pet_space,
  };

  let matched = 0, total = 0;
  for (const feat of desiredFeatures) {
    const check = featureMap[feat];
    if (check) { total++; if (check(candidate)) matched++; }
  }
  if (total === 0) return 60;
  return Math.round(20 + (matched / total) * 80);
}

// === AMENITIES SCORE ===
function calculateAmenitiesScore(myProperty: Property, candidateProperty: Property): number {
  const myAm = (myProperty.nearby_amenities as string[] | null) || [];
  const candAm = (candidateProperty.nearby_amenities as string[] | null) || [];
  if (myAm.length === 0 && candAm.length === 0) return 60;
  if (myAm.length === 0) return Math.min(100, 50 + candAm.length * 8);

  const mySet = new Set(myAm);
  let matchCount = 0;
  for (const a of candAm) { if (mySet.has(a)) matchCount++; }
  let score = Math.round(20 + (matchCount / myAm.length) * 60);
  if (candAm.length > myAm.length) score = Math.min(100, score + 10);
  const keyAm = ["Metrô/Trem", "Escolas", "Hospitais", "Supermercados"];
  const candSet = new Set(candAm);
  for (const k of keyAm) { if (candSet.has(k)) score = Math.min(100, score + 3); }
  return Math.min(100, score);
}

// === LIQUIDITY ===
export function calculateLiquidity(
  property: Property, allProperties: Property[], allPrefs: Map<string, SwapPref>
): { level: "Alta" | "Média" | "Baixa"; compatibleCount: number; score: number } {
  const myPrefs = allPrefs.get(property.id) || null;
  let highCount = 0, medCount = 0;
  for (const c of allProperties) {
    if (c.id === property.id || c.user_id === property.user_id) continue;
    const r = calculateCompatibility(property, myPrefs, c, allPrefs.get(c.id) || null);
    if (r.score >= 70) highCount++;
    else if (r.score >= 50) medCount++;
  }
  const compatibleCount = highCount + medCount;
  const score = Math.min(100, highCount * 15 + medCount * 5);
  const level = score >= 60 ? "Alta" : score >= 30 ? "Média" : "Baixa";
  return { level, compatibleCount, score };
}

// === CHAIN SWAPS ===
export interface ChainSwap { properties: Property[]; totalScore: number; }

export function detectChainSwaps(properties: Property[], prefsMap: Map<string, SwapPref>, maxChains = 8): ChainSwap[] {
  const chains: ChainSwap[] = [];
  const n = properties.length;
  if (n < 3) return chains;

  const edges = new Map<string, { target: string; score: number }[]>();
  for (const propA of properties) {
    const adj: { target: string; score: number }[] = [];
    for (const propB of properties) {
      if (propA.user_id === propB.user_id) continue;
      const r = calculateCompatibility(propA, prefsMap.get(propA.id) || null, propB, prefsMap.get(propB.id) || null);
      if (r.score >= 45) adj.push({ target: propB.id, score: r.score });
    }
    adj.sort((a, b) => b.score - a.score);
    edges.set(propA.id, adj.slice(0, 12));
  }

  const propMap = new Map(properties.map(p => [p.id, p]));
  const seen = new Set<string>();

  function getEdgeScore(fromId: string, toId: string): number | null {
    return (edges.get(fromId) || []).find(e => e.target === toId)?.score ?? null;
  }

  function tryAddChain(ids: string[]) {
    if (chains.length >= maxChains) return;
    const key = [...ids].sort().join("-");
    if (seen.has(key)) return;
    let total = 0;
    for (let i = 0; i < ids.length; i++) {
      const s = getEdgeScore(ids[i], ids[(i + 1) % ids.length]);
      if (!s || s < 40) return;
      total += s;
    }
    const avg = total / ids.length;
    if (avg < 48) return;
    seen.add(key);
    chains.push({ properties: ids.map(id => propMap.get(id)!), totalScore: Math.round(avg) });
  }

  for (const propA of properties) {
    const adjA = edges.get(propA.id) || [];
    for (const { target: bId } of adjA.slice(0, 8)) {
      const adjB = edges.get(bId) || [];
      for (const { target: cId } of adjB.slice(0, 6)) {
        if (cId === propA.id) continue;
        if (propMap.get(cId)?.user_id === propA.user_id) continue;
        tryAddChain([propA.id, bId, cId]);
      }
    }
  }

  chains.sort((a, b) => b.totalScore - a.totalScore);
  return chains.slice(0, maxChains);
}
